<template>
  <application-page>
    <template v-if="appData" #ad>
      <!-- <p class="temp-id">Application ID: {{ application.appId || "N/A" }}</p> -->
      <h1>{{ leftPanelText }}</h1>
    </template>
    <div v-if="previousLoading || currentStep > statesPath.length" class="previous-loader">
      <circle-loader />
    </div>
    <application-steps v-else :step-scroll="stepScroll">
      <template v-if="appData">
        <application-step
          v-for="(state, index) in statesPath"
          :key="index"
          :route="state.route"
          :application="appData"
          :path="path"
          :loading="loading"
          :previous-loading="previousLoading"
          :is-current="isCurrent(state)"
          @previous="previous"
          @complete="stepComplete"
        >
          <circle-loader v-if="loading" :inverse="true" />
        </application-step>
      </template>
    </application-steps>
  </application-page>
</template>

<script lang="ts">
import { defineComponent, computed, watch } from "vue";
import { mapActions, mapState, useStore } from "vuex";
import { $api } from "@/services/api";
import * as jsonPatch from "fast-json-patch";
import useLeftPanelText from "@/use/leftPanelText";
import ApplicationPage from "@/components/shared/ApplicationPage.vue";
import ApplicationSteps from "@/components/shared/ApplicationSteps.vue";
import ApplicationStep from "@/components/shared/ApplicationStep.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import type {
  AppData,
  Applicant,
  ApplicantObserver,
  Application,
  ComparatorFunction,
  State,
  Student,
  Transition,
  Validation,
} from "@/models/application";
import type { UpdateApplicationRequest } from "@/models/api";
import useAuthentication from "@/use/authentication";
import useApplication from "@/use/application";
import useApplicationData from "@/composables/application/useApplicationData";
import useApplicationPatch from "@/composables/useApplicationPatch";
import useApplicantPatch from "@/composables/useApplicantPatch";
import { resetParams } from "@/router";
import { resetKeyIds } from "@/config/constants";

export default defineComponent({
  setup() {
    const store = useStore();
    const { flowType, setFlowType, isRefi, programTypeId, statesSeen, applicantCount } = useApplicationData();

    const { getCurrentText } = useLeftPanelText();
    const { handleLoginRedirect, handleLoginRedirectWithParams } = useAuthentication();
    const { getLenderConfigurationAsync } = useApplication();
    const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
    const oidcIsAuthenticated = computed(() => store.getters["oidcStore/oidcIsAuthenticated"]);
    const authenticatedUser = computed<boolean>(() => oidcUser.value !== null && oidcIsAuthenticated.value);
    const applicantAuthEnabled = computed<boolean>(() => store.state.appData.applicantAuthEnabled || false);

    watch(programTypeId, async (newValue, oldValue) => {
      if ((!oldValue || oldValue === 0) && newValue > 0) {
        await getLenderConfigurationAsync({
          appId: store.state.appData.application.id,
          lenderId: store.state.appData.application.lenderId,
          productTypeId: store.state.appData.application.productTypeId,
          programTypeId: newValue,
        });
      }
    });

    return {
      applicantCount,
      store,
      applicantAuthEnabled,
      flowType,
      getCurrentText,
      oidcUser,
      oidcIsAuthenticated,
      authenticatedUser,
      handleLoginRedirect,
      handleLoginRedirectWithParams,
      isRefi,
      programTypeId,
      resetParams,
      setFlowType,
      statesSeen,
    };
  },
  components: {
    ApplicationPage,
    ApplicationSteps,
    ApplicationStep,
    CircleLoader,
  },
  data: () => ({
    currentStep: 1,
    path: "",
    loading: false,
    previousLoading: false,
    statesPath: [] as State[],
    compareAppData: {} as AppData,
    appObserver: {} as jsonPatch.Observer<Application>,
    applicantObservers: [] as ApplicantObserver[],
    updatedApplicant: undefined as any,
    updatedStudent: undefined as undefined | Student,
  }),
  computed: {
    ...mapState(["appData", "leftPanelText"]),
    states(): any[] {
      return this.appData.statesSeen || [];
    },
    stepScroll(): string {
      return `${(this.currentStep - 1) * 100}%`;
    },
    currentApplicantTypeName(): string {
      if (this.appData.currentState?.name.startsWith("Coborrower - Borrower", 0)) {
        return "Primary Applicant";
      } else if (this.appData.currentState?.name.startsWith("Coborrower -", 0)) {
        return "Co-Applicant";
      } else if (this.appData.currentState?.name.startsWith("Borrower - Coborrower", 0)) {
        return "Co-Applicant";
      } else if (this.appData.currentState?.name.startsWith("Borrower -", 0)) {
        return "Primary Applicant";
      } else {
        return "";
      }
    },
  },
  methods: {
    ...mapActions(["setAppData", "setLeftPanelText"]),
    async previous() {
      try {
        this.previousLoading = true;
        this.appData.currentState = this.appData.previousState;
        this.appData.statesSeen.push(this.appData.currentState);

        // Update currentStep index first update statesPath
        const newStates = this.statesPath;

        // Push the current state to the stack if we are at 0
        // If the length is greater than 1 we already have what we need
        if (newStates.length <= 1) {
          newStates.unshift(this.appData.currentState);
          this.currentStep--;
        }

        this.statesPath = newStates;

        // Set the current state order
        this.appData.currentState.order = this.appData.statesSeen.length - 1;
        await this.updateAppData();
        this.previousLoading = false;

        this.updateLeftPanel();
        this.currentStep--;
        this.statesPath.pop();
        console.log("PREVIOUS COMPLETE", this.currentStep, this.statesPath);

        // set the states path
        if (this.statesPath.length === 1 && this.appData.previousState) {
          this.statesPath.unshift(this.appData.previousState);
          this.currentStep++;
        }
      } catch (error) {
        // Automatically refresh component to get around the Uncaught TypeError
        location.reload();
      }
    },
    async stepComplete(data: any) {
      this.loading = true;
      console.log("stepComplete() event data", data);

      // 1. Verify what the current state is, and compare it to the last item in statesSeen
      // 2. If last state seen, grab the right transition and move it into statesSeen
      if ((this.appData.transitions || []).length > 0) {
        let state: any;

        if (this.appData.transitions.length > 1) {
          let stateSet = false;
          this.appData.transitions = this.appData.transitions.sort((a: any, b: any) => {
            if (a.comparatorFunction === null) {
              return 1;
            }
            if (b.comparatorFunction === null) {
              return -1;
            }
            if (b.comparatorFunction) {
              return 0;
            }
          });

          this.appData.transitions.forEach((transition: Transition, index: number) => {
            if (stateSet) {
              return;
            }

            if (transition.comparatorFunction && transition.comparatorFunction.trim() !== "") {
              try {
                const comparison: ComparatorFunction = JSON.parse(transition.comparatorFunction);
                if (comparison.validation && comparison.validation.length > 0) {
                  let valid = comparison.type === "AND";
                  comparison.validation.forEach((validation: Validation) => {
                    let compareData = data[validation.property];

                    if (compareData && typeof compareData === "string") {
                      compareData = compareData.trim();
                    }

                    // Verify which type of comparison we need to perform
                    const compareValue =
                      validation.operator === "ne"
                        ? compareData !== validation.value
                        : compareData === validation.value;

                    // Take comapreValue and append it to current valid state
                    valid = comparison.type === "AND" ? valid && compareValue : valid || compareValue;
                  });

                  if (valid) {
                    state = transition;
                    stateSet = true;
                  }
                }
              } catch (ex) {
                // not proper JSON stored. Just skipping
                console.error("Could not parse comparator function", ex);
              }
            }

            // Make sure a transition gets added no matter what
            if (state === undefined && index === this.appData.transitions.length - 1) {
              state = transition;
              stateSet = true;
            }
          });
        } else {
          state = this.appData.transitions[0];
        }

        if (state !== undefined) {
          this.appData.statesSeen.push({
            name: state.name,
            route: state.route,
            fsStateID: state.fsStateID,
            // Get current length since order starts at 0
            order: this.appData.statesSeen.length,
            programTypeId: state.programTypeId,
          });
          this.appData.previousState = this.appData.currentState;
        }
      }

      if (data?.updatedPerson) {
        this.updatedApplicant = data.updatedPerson;
      }

      if (data?.updatedStudent) {
        this.updatedStudent = data?.updatedStudent;
      }

      // 4. Update the Application data
      await this.updateAppData();

      // Add step first in an attempt to provide cleaner transition
      this.statesPath.push(this.appData.currentState);
      this.currentStep++;

      console.log("STEP COMPLETE", this.currentStep, this.statesPath);

      // removes the original previous state from the statesPath arr
      if (this.statesPath.length >= 1 && this.appData.previousState) {
        this.statesPath.shift();
        this.currentStep--;
      }

      // Clear query after first step / after starting a new application
      const query = Object.assign({}, this.$route.query);
      if (query?.lid && query?.productTypeId && query?.leadSourceId && query?.programTypeId) {
        localStorage.setItem("product", `${query?.productTypeId}`);

        delete query?.leadSourceId;
        delete query?.productTypeId;
        delete query?.programTypeId;
        delete query?.lid;
        this.$router.replace({ query });
      }

      this.loading = false;
    },
    isCurrent(state: State) {
      const current = this.appData.currentState;
      return current.fsStateID === state.fsStateID;
    },
    updateLeftPanel() {
      this.setLeftPanelText(this.getCurrentText(this.appData.currentState?.route || "", this.appData));
    },
    setAppDataDisplay() {
      //Pull application out of state
      if (this.appData) {
        this.refreshObservers();

        if (this.appData.currentState && this.statesPath.length <= 0) {
          this.statesPath = [this.appData.currentState];
        }

        // adds previous state to beginning of statesPath
        if (this.statesPath.length === 1 && this.appData.previousState) {
          this.statesPath.unshift(this.appData.previousState);
          this.currentStep++;
        }

        this.updateLeftPanel();
      }
    },
    refreshObservers() {
      if (this.appData) {
        if (this.appData.application) {
          if (this.appData.application.schoolConfiguration === null) {
            this.appData.application.schoolConfiguration = undefined;
          }

          this.appObserver = jsonPatch.observe(this.appData.application);
        }

        if (this.appData.applicants && this.appData.applicants.length > 0) {
          // Loop over array and create observers for each
          this.applicantObservers = this.appData.applicants.map((applicant: Applicant) => {
            return {
              applicantId: applicant.id,
              observer: jsonPatch.observe<Applicant>(applicant),
            };
          });
        }
      }
    },
    // person is an optional param
    async updateAppData() {
      //precaution for CoreDataApi
      if (this.appData.applicationId === undefined && this.appData.appId) {
        this.appData.applicationId = this.appData.appId;
        console.log("SETTING APP ID FOR COREDATAAPI", this.appData.applicationId);
      }

      // 1. Gather the Application Patch object
      console.log("Gather the Application Patch object", this.appData.application);
      const applicationPatch = jsonPatch.generate(this.appObserver);

      // 2. Gather any Applicant patches
      const { gatherApplicantPatches } = useApplicantPatch(this.store, this.updatedApplicant);

      const applicantPatches = gatherApplicantPatches();

      const { filterApplicationPatches } = useApplicationPatch();

      const filteredApplicationPatches = filterApplicationPatches(applicationPatch);

      console.log("APPLICATION PATCH", { applicationPatch, filteredApplicationPatches });

      const updatedAppData = await $api.applications.updateApplication({
        applicationId: this.appData.id,
        applicationPatch: filteredApplicationPatches,
        applicantPatches,
        currentState: this.appData.currentState,
        previousState: this.appData.previousState,
        statesSeen: this.appData.statesSeen,
        flowType: this.appData?.flowType || this.flowType,
      } as UpdateApplicationRequest);

      console.log("updateAppData() UPDATED APP", updatedAppData);

      if (!updatedAppData?.error || !updatedAppData.id) {
        await this.setAppData(updatedAppData);

        this.updatedApplicant = undefined;
        this.updatedStudent = undefined;
      } else {
        alert("Something has gone wrong");
      }
    },
  },
  watch: {
    appData() {
      console.log("Application Update");
      this.setAppDataDisplay();
    },
    "statesSeen.length": {
      handler(newValue, oldValue) {
        console.log("states seen length changed", { oldValue, newValue });
        if (newValue === oldValue) {
          return;
        }

        if (oldValue === 1 && newValue === 2 && !this.isRefi) {
          this.resetParams();
        }
      },
    },
    flowType: {
      handler(newValue, oldValue) {
        if (newValue === oldValue) {
          return;
        }
        this.setFlowType(newValue);
        this.appData.flowType = newValue;
      },
      immediate: true,
    },
  },
  unmounted() {
    console.log("Application.vue unmounted");

    resetKeyIds.forEach((k: string) => {
      localStorage.removeItem(k);
    });
  },
});
</script>

<style lang="scss">
.previous-loader {
  position: relative;
  width: 65%;
  height: 100%;
  display: block;
  overflow: hidden;
}
</style>
